/**
 * Hotkeys.ts
 *
 * Defines application-level keyboard shortcuts and utilities for displaying hotkeys in
 * user-friendly format. Abstracts platform-specific modifier keys (Cmd on Mac, Ctrl on Windows).
 *
 * See also:
 * - @./types.ts - HotKeyWithAction type definition.
 */

// TODO: Grab them all and check that none is duplicated

import type { HotKey } from "@/endlaFunctions/customTypings/atoms";
import type { Keys } from "react-hotkeys-hook/dist/types";
import type { HotKeyWithAction } from "./types";

const IS_MAC = navigator.userAgent.includes("Mac");

const MOD_KEY = IS_MAC ? "⌘" : "Ctrl";
const MOD = "mod";

// const _HOTKEY_MODIFIER_ALT = "alt";
// const HOTKEY_MODIFIER_CTRL = "ctrl";

/* App Level */

export const HOTKEY_DATA_UPLOAD: HotKey = `${MOD}+U`;

/* Table hotkeys */
export const HOTKEY_DATA_TABLE_SCROLL_TO_COLUMN = `${MOD}+shift+C`;

/* Scene hotkeys */
export const HOTKEY_SCENE_FOCUS: HotKey = "f";
export const HOTKEY_TOGGLE_X_RAY: HotKey = "x";
export const HOTKEY_TOGGLE_SCENE_POINTER_INSPECTOR: HotKey = "i";
export const HOTKEY_TOGGLE_SCENE_POINTER_LOCATION: HotKey = "l";

export const HOTKEY_SIDEBAR = `${MOD}+b`;
export const HOTKEY_LASSO_SELECT = `${MOD}+L`;
export const HOTKEY_DEBUG: HotKey = `${MOD}+\\`;
export const HOTKEY_DEBUG_MOUSE: HotKey = `${MOD}+]`;
export const HOTKEY_RESET_TOOL = `${MOD}+Q`;

export const HOTKEY_ESCAPE_CAMERA_PATH_LOCK = `${MOD}+Escape`;
export const HOTKEY_ESCAPE_INSPECTOR_LOCK = "escape";
export const HOTKEY_COMMAND_PALETTE = `${MOD}+J`;

export const HOTKEY_HELP_GRID_OVERLAY = `${MOD}+G`;
export const HOTKEY_SAVE_DASHBOARD = `${MOD}+S`;

export const HARD_RESET_HOTKEY = `${MOD}+~`;

const DISPLAY_MAP: Record<string, string> = {
  [MOD]: MOD_KEY,
} as const;

/**
 * Formats a hotkey or chord sequence into a human-readable display string.
 * Maps keyboard symbols (e.g., "mod" → "⌘" on Mac or "Ctrl" on Windows) and
 * navigation keys (e.g., "arrowup" → "↑").
 *
 * @param hotkey - A single key string, modifier+key combination, or array of chord sequences.
 * @returns Formatted display string with chords separated by " or ".
 */
export function displayHotKey(hotkey?: Keys): string {
  // split on the + and then map to the display version
  //   check if hotkey is string
  if (hotkey === undefined) {
    return "";
  }

  if (typeof hotkey === "string") {
    return hotkey
      .split("+")
      .map((key) => DISPLAY_MAP[key] ?? key)
      .join(" ");
  }
  return hotkey.map((chord) => displayHotKey(chord)).join(" or ");
}

/**
 * Extracts and displays the hotkey binding from a HotKeyWithAction object.
 *
 * @param hotKeyWithAction - Object containing a binding property and associated action.
 * @returns Formatted display string for the binding.
 */
export function displayHotKeyBinding(hotKeyWithAction?: HotKeyWithAction): string {
  return displayHotKey(hotKeyWithAction?.binding);
}
